<template>
  <e-form v-model="isValid" @submit="submit">
    <e-radio-group
      v-if="!serviceGroup"
      v-model="data.type"
      :items="radioItems"
      inline
      class="mb-8"
      @update:modelValue="onTypeChange"
    />
    <e-select
      v-if="data.type === 2"
      v-model="activeSample"
      :items="samples"
      :loading="isContranctServiceGroupLoading"
      item-title="title"
      return-object
      label="Группа услуг / Тарифный план"
      class="mb-6"
      @update:model-value="onSampleUpdate"
    >
      <template #item="{ item }">
        <div>
          <div class="input-text">{{ item.title }}</div>
          <div class="paragraph-xs text-secondary">
            <span v-for="(service, index) in item.raw.services" :key="service.cid"
              ><template v-if="index > 0">, </template>{{ service.shortName }}
            </span>
          </div>
        </div>
      </template>
    </e-select>
    <multilanguage-field
      v-model="data.name"
      :rules="[required()]"
      :common-rules="[maxLength(255)]"
      label="Наименование группы услуг"
      class="mb-6"
    />
    
    <div class="paragraph-l font-600">Услуги</div>

    <div class="paragraph-s_h text-secondary mt-2">
      Порядок и указание первой / последней услуги, предоставляемой в течение дня, формируется автоматически. Если данный порядок некорректен просьба обратиться в 
      <router-link :to="{ name: RouteNames.Support }" class="link">Поддержку</router-link> 
    </div>

    <service-groups-input 
      v-model="data.services" 
      :tariff-schedule-cid="props.tariffScheduleCid"
      :hotel-set-cid="props.hotelSetCid"
      class="mt-4 mb-6" 
    />

    <multilanguage-field
      v-model="data.description"
      label="Описание группы услуг (необязательно)"
      :common-rules="[maxLength(255)]"
      textarea
      class="mb-6"
    />
    <div class="d-flex">
      <v-spacer />
      <e-btn variant="secondary" class="mr-4" @click="cancel"> Отмена</e-btn>
      <e-btn v-if="serviceGroup" :loading="isLoading" variant="primary" type="submit"> Сохранить </e-btn>
      <template v-else>
        <e-btn v-if="!showConfirmation" :loading="isLoading" variant="primary" type="submit"> Создать </e-btn>

        <confirmation-dialog v-else :action="submit" confirm-text="Создать">
          <template #activator>
            <e-btn :loading="isLoading" variant="primary"> Создать </e-btn>
          </template>

          <div class="h3 mb-6">Создание группы услуг</div>
          <div class="text-secondary">
            Изменение Группы услуг после создания будет доступно исключительно через обращение в <b>Поддержку</b>.
            <br />
            <br />
            <b>Убедитесь, что внесли правильные данные!</b>
          </div>
        </confirmation-dialog>
      </template>
    </div>
  </e-form>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { ArrayElementType } from '@/core/types/gengeric';
import type { MultilanguageValue } from '@/core/utils/languages';
import ConfirmationDialog from '@/core/components/modals/ConfirmationDialog.vue';
import { EBtn, EForm, ERadioGroup, ESelect, MultilanguageField } from '@/core/components/ui';
import { deepCloneObject } from '@/core/utils/object';
import { maxLength, required } from '@/core/utils/validation';
import ServiceGroupsInput from '@/parts/tariffs/components/ServiceGroupsInput.vue';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { useSnackbar } from '@/core/composables/useSnackbar';
import {
  type AddServiceGroupPayload,
  type ContractServiceGroups,
  type ServiceGroup,
  useServiceGroupsStore,
} from '@/parts/tariffs/store/serviceGroups';

const props = defineProps<{
  tariffScheduleCid?: string;
  hotelSetCid: string;
  serviceGroup?: ServiceGroup;
  showConfirmation?: boolean;
}>();

const emit = defineEmits<{
  (event: 'success:add', data: AddServiceGroupPayload): void;
  (event: 'success:update', data: ServiceGroup): void;
  (event: 'cancel'): void;
}>();

const { showSnackbar } = useSnackbar();

const { 
  contractServiceGroups, 
  createServiceGroup, 
  editTariffScheduleServiceGroupUpdate,
  getContractServiceGroups,
} = useServiceGroupsStore();

const isValid = ref(false);
const isLoading = ref(false);

const data = ref({
  type: 1,
  name: null as MultilanguageValue[] | null,
  services: [] as string[],
  description: null as MultilanguageValue[] | null,
});

const setDataValue = () => {
  if (!props.serviceGroup) {
    return;
  }

  const d = deepCloneObject(props.serviceGroup);

  data.value.type = 1;
  data.value.name = d.name;
  data.value.services = d.services.map((x) => x.cid);
  data.value.description = d.description;
};

setDataValue();

type Sample = ArrayElementType<ArrayElementType<ContractServiceGroups>['serviceGroups']> & {
  title: string;
};

const activeSample = ref<Sample>();

const samples = computed(
  () =>
    contractServiceGroups.value?.reduce((res, item) => {
      const newItems: Sample[] = item.serviceGroups.map((serviceGroup) => ({
        title: `${serviceGroup.name[0].value} / ${item.tariffSchedule.name}`,
        ...serviceGroup,
      }));

      res = [...res, ...newItems];

      return res;
    }, [] as Sample[]) || [],
);

const onSampleUpdate = () => {
  if (!activeSample.value) {
    return;
  }

  data.value.name = activeSample.value.name;
  data.value.description = activeSample.value.description;
  data.value.services = activeSample.value.services.map((service) => service.cid);
};

const submit = async () => {
  if (!isValid.value) {
    return;
  }

  isLoading.value = true;

  const payload = {
    tariffScheduleCid: props.tariffScheduleCid,
    hotelSetCid: props.hotelSetCid,
    serviceGroupCid: '',
    serviceGroup: {
      name: data.value.name!,
      description: data.value.description!,
      isActive: true,
      serviceCids: data.value.services,
    },
  };

  if (props.serviceGroup) {
    payload.serviceGroupCid = props.serviceGroup.cid;
    const result = await editTariffScheduleServiceGroupUpdate(payload);

    if (result?.data?.updateTariffScheduleServiceGroup.serviceGroup) {
      emit('success:update', result.data.updateTariffScheduleServiceGroup.serviceGroup);
    }
  } else {
    const result = await createServiceGroup(payload);
    
    if (result?.data) {
      emit('success:add', result.data.addTariffScheduleServiceGroup);

      showSnackbar({
        type: 'success',
        title: 'Новая группа услуг добавлена',
        description: data.value.name?.[0].value,
      });
    }
  }

  isLoading.value = false;
};

const cancel = () => {
  emit('cancel');
};

const radioItems = [
  {
    title: 'Новая',
    value: 1,
  },
  {
    title: 'По шаблону других групп услуг ',
    value: 2,
  },
];

const isContranctServiceGroupLoading = ref(false);

const onTypeChange = async () => {
  if (data.value.type === 2) {
    isContranctServiceGroupLoading.value = true;
    await getContractServiceGroups();
    isContranctServiceGroupLoading.value = false;
  }
};
</script>

<style scoped lang="scss"></style>
